/** Shared utilities for {@link VolunteerRow}, {@link VolunteerCard} and {@link VolunteersDataTable}. */
import { Mail, Phone, type LucideIcon } from 'lucide-react';
import type { InterfaceElementResponseDto } from '@/types/interface-elements.types';
import type { GetUsersParams, PrimaryContactKind } from '@/types/user.types';
import { availabilityKeysToDays } from '@/utils/common-utils';
import { resolveListDateRange } from '@/utils/list-date-range-utils';
import { resolveUserContacts } from '@/utils/user-contact-utils';
import type { VolunteerFilters } from './volunteer-configs';

/** Primary contact icon + value, with optional secondary shown under it. */
export interface VolunteerContactDisplay {
  icon: LucideIcon;
  value: string;
  secondaryValue: string | null;
}

/** Phone vs email from `primaryContactKind`; `N/R` when empty. */
export function deriveVolunteerContact(volunteer: {
  email?: string | null;
  phone?: string | null;
  primaryContactKind?: PrimaryContactKind | null;
}): VolunteerContactDisplay {
  const resolved = resolveUserContacts(
    volunteer.primaryContactKind,
    volunteer.email,
    volunteer.phone,
  );
  return {
    icon: resolved.kind === 'phone' ? Phone : Mail,
    value: resolved.primary?.value ?? 'N/R',
    secondaryValue: resolved.secondary?.value ?? null,
  };
}

/**
 * Pure helper for the canEdit / canDelete memos shared between {@link VolunteerCard} and {@link VolunteerRow}.
 * Returns whether a given permission subtype is granted for any of the volunteer's roles.
 */
export function resolveVolunteerPermission(
  userRoles: Array<{ role: { code: string } }> | undefined,
  interfaceElements: InterfaceElementResponseDto[],
  subtype: 'edit-role' | 'delete-role',
  defaultRole = 'volunteer',
): boolean {
  const codes = userRoles?.map((ur) => ur.role.code) ?? [];
  const codesToCheck = codes.length > 0 ? codes : [defaultRole];
  return interfaceElements.some((el) => {
    const targetRole = (el.config as { subtype?: string; targetRole?: string } | undefined)
      ?.targetRole;
    return (
      el.elementType === 'action' &&
      (el.config as { subtype?: string } | undefined)?.subtype === subtype &&
      targetRole != null &&
      codesToCheck.includes(targetRole)
    );
  });
}

/** Formats a date value as dd/mm/yyyy French format, or "—". */
export function formatVolunteerShortDate(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Custom entry dates override the period preset. */
export function resolveVolunteerListDateRange(filters: VolunteerFilters) {
  const preset = resolveListDateRange(filters);
  return {
    dateFrom: filters.entryDateFrom ? new Date(filters.entryDateFrom) : preset.dateFrom,
    dateTo: filters.entryDateTo ? new Date(filters.entryDateTo) : preset.dateTo,
  };
}

/** Map UI filters to volunteer list query params. */
function toVolunteerFilterParams(
  filters: VolunteerFilters,
  roleCodes?: string[],
): Omit<GetUsersParams, 'page' | 'pageSize'> {
  const { dateFrom, dateTo } = resolveVolunteerListDateRange(filters);
  const days = availabilityKeysToDays(filters.availability ?? []);
  return {
    search: filters.search?.trim() || undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    subjectIds: filters.subjects.length > 0 ? filters.subjects : undefined,
    establishmentId: filters.establishmentId,
    dateFrom,
    dateTo,
    availabilityDays: days.length > 0 ? days : undefined,
    roleCodes: roleCodes && roleCodes.length > 0 ? roleCodes : undefined,
    engagementStartDateFrom: filters.engagementStartDateFrom,
    engagementStartDateTo: filters.engagementStartDateTo,
    engagementEndDateFrom: filters.engagementEndDateFrom,
    engagementEndDateTo: filters.engagementEndDateTo,
    tutorExitDateFrom: filters.exitDateFrom,
    tutorExitDateTo: filters.exitDateTo,
    tutorExitReason: filters.exitReason || undefined,
  };
}

/** Map UI filters + pagination to volunteer list query params. */
export function buildVolunteerListQueryParams(
  filters: VolunteerFilters,
  options: { page?: number; pageSize?: number; roleCodes?: string[] } = {},
): GetUsersParams {
  return {
    ...toVolunteerFilterParams(filters, options.roleCodes),
    page: options.page,
    pageSize: options.pageSize,
  };
}

/** Volunteer export params (same filters as the list, no pagination). */
export function buildVolunteerExportQueryParams(
  filters: VolunteerFilters,
  roleCodes?: string[],
): Omit<GetUsersParams, 'page' | 'pageSize'> {
  return toVolunteerFilterParams(filters, roleCodes);
}

/** True when any volunteer chip / date / establishment / search filter is set. */
export function hasVolunteerActiveFilters(filters: VolunteerFilters): boolean {
  return (
    !!filters.search?.trim() ||
    filters.statuses.length > 0 ||
    filters.subjects.length > 0 ||
    (filters.availability != null && filters.availability.length > 0) ||
    filters.establishmentId !== undefined ||
    filters.dateRange !== 'all' ||
    filters.customDateFrom !== undefined ||
    filters.customDateTo !== undefined ||
    !!filters.entryDateFrom ||
    !!filters.entryDateTo ||
    !!filters.engagementStartDateFrom ||
    !!filters.engagementStartDateTo ||
    !!filters.engagementEndDateFrom ||
    !!filters.engagementEndDateTo ||
    !!filters.exitDateFrom ||
    !!filters.exitDateTo ||
    !!filters.exitReason
  );
}

/** Human-readable seniority from an engagement start date. */
export function computeVolunteerSeniority(startDate: Date | string | null | undefined): string {
  if (!startDate) return '—';
  const start = new Date(startDate as string);
  if (Number.isNaN(start.getTime())) return '—';
  const now = new Date();
  const months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 1) return '< 1 mois';
  if (months < 12) return `${months} mois`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yearLabel = `${years} an${years > 1 ? 's' : ''}`;
  if (rem > 0) return `${yearLabel} ${rem} mois`;
  return yearLabel;
}

/** Establishment chips: `name (UAI)` when a code is present. */
export function mapVolunteerEstablishmentItems(
  userEstablishments:
    | Array<{
        establishment?: { id?: string; name?: string; uaiCode?: string | null } | null;
      }>
    | undefined,
): Array<{ id: string; name: string }> {
  return (
    userEstablishments
      ?.map((ue) => ue.establishment)
      .filter((e): e is NonNullable<typeof e> & { id: string; name: string } =>
        Boolean(e?.id && e?.name),
      )
      .map((e) => ({
        id: e.id,
        name: e.uaiCode ? `${e.name} (${e.uaiCode})` : e.name,
      })) ?? []
  );
}
